import React, { useCallback, useEffect, useState } from "react";
import { cls } from "app/utils";
import { useTranslation } from "react-i18next";
import { fetchGet, fetchPut, fetchDelete } from "app/utils/request";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { clearFamilyRosterCache } from "app/routes/Sessions/familyRosterCache";
import { Loading } from "app/components/Wrappers";
import AccountWrap from "..";
import Button from "app/components/Button/ButtonVariant";
import { Toast } from "app/components/helpers";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import FamilyMemberCard from "./_components/FamilyMemberCard";
import FamilyMemberModal from "./_components/FamilyMemberModal";
import FamilyMemberAddedModal from "./_components/FamilyMemberAddedModal";
import {
  FamilyMember,
  FamilyMemberFormData,
  FamilyMemberModalMode,
  FamilyMemberSubmitResult,
  RemovalBlockers,
} from "./types";
import { createFamilyMember, familyMembershipErrorMessage } from "./utils";
import styles from "./styles.module.scss";

const ENDPOINT = "family_memberships";
const API_OPTS = { version: 2 };

interface ModalState {
  isOpen: boolean;
  mode: FamilyMemberModalMode;
  member: FamilyMember | null;
}

const FamilyEdit = () => {
  const { t } = useTranslation();

  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    mode: "create",
    member: null,
  });
  const [memberToDelete, setMemberToDelete] = useState<FamilyMember | null>(
    null,
  );
  // CFTB-3348: a member with live commitments can't be removed — set when the
  // pre-check refuses, drives the explanatory modal.
  const [removalBlocked, setRemovalBlocked] = useState<{
    member: FamilyMember;
    blockers: RemovalBlockers["blockers"];
  } | null>(null);
  const [toastAlert, setToastAlert] = useState<string | null>(null);
  const [toastError, setToastError] = useState<string | null>(null);
  // Name of a just-added member, drives the success popup (CFTB-3274).
  const [addedMemberName, setAddedMemberName] = useState<string | null>(null);

  const loadMembers = useCallback(async () => {
    try {
      const roster = await fetchGet<FamilyMember>(ENDPOINT, {}, API_OPTS);
      // The roster includes the owner (role: null); show only managed members.
      const list = (Array.isArray(roster) ? roster : []).filter(
        (m) => m.role != null,
      );
      setMembers(list);
    } catch {
      setToastError(t("Could not load your family members."));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const openCreate = () =>
    setModal({ isOpen: true, mode: "create", member: null });

  const openEdit = (member: FamilyMember) =>
    setModal({ isOpen: true, mode: "edit", member });

  const closeModal = () => setModal((prev) => ({ ...prev, isOpen: false }));

  // Resolves { ok: true } on success (modal closes); on failure the modal stays
  // open and surfaces the server validation message when there is one.
  const handleSubmit = async (
    data: FamilyMemberFormData,
  ): Promise<FamilyMemberSubmitResult> => {
    try {
      if (modal.mode === "create") {
        await createFamilyMember(data);
        setAddedMemberName(`${data.firstName} ${data.lastName}`.trim());
      } else if (modal.member) {
        const attributes: Record<string, unknown> = {
          first_name: data.firstName,
          last_name: data.lastName,
          date_of_birth: data.dateOfBirth,
        };
        if (data.avatarId) {
          attributes.avatar_id = data.avatarId;
        }
        await fetchPut(
          `${ENDPOINT}/${modal.member.id}`,
          { family_membership: attributes },
          API_OPTS,
        );
        setToastAlert(t("Family member updated."));
      }
      // CFTB-3347: the booking pickers read a shared roster cache; invalidate it
      // so a member added/edited here appears in their selection options.
      clearFamilyRosterCache();
      await loadMembers();
      closeModal();
      return { ok: true };
    } catch (err) {
      return { ok: false, message: familyMembershipErrorMessage(err) ?? undefined };
    }
  };

  // CFTB-3348: removing a member with future sessions, active registrations, or
  // active packages would orphan those records (the head then 403s on their
  // package/booking views). Ask coaching_service first; only open the confirm
  // dialog when the member is clear to remove, otherwise explain why not.
  const requestDelete = async (member: FamilyMember) => {
    try {
      const { removable, blockers } = (await schedulingFetchGet<RemovalBlockers>(
        `family_members/${member.userId}/removal_blockers`,
      )) as RemovalBlockers;
      if (removable) {
        setMemberToDelete(member);
      } else {
        setRemovalBlocked({ member, blockers });
      }
    } catch {
      // Don't fall through to deletion on a failed check — that's the very bug
      // we're guarding against. Let the user retry.
      setToastError(t("Couldn't check whether this member can be removed."));
    }
  };

  const blockedReasons = (blockers: RemovalBlockers["blockers"]): string => {
    const reasons: string[] = [];
    if (blockers.upcomingSessions > 0) {
      reasons.push(
        t("{{count}} upcoming session(s)", { count: blockers.upcomingSessions }),
      );
    }
    if (blockers.programRegistrations > 0) {
      reasons.push(
        t("{{count}} program registration(s)", {
          count: blockers.programRegistrations,
        }),
      );
    }
    if (blockers.activePackages > 0) {
      reasons.push(
        t("{{count}} active package(s)", { count: blockers.activePackages }),
      );
    }
    return reasons.join(", ");
  };

  const confirmDelete = async () => {
    if (!memberToDelete) {
      return;
    }
    const target = memberToDelete;
    setMemberToDelete(null);
    try {
      await fetchDelete(`${ENDPOINT}/${target.id}`, {}, API_OPTS);
      setToastAlert(t("Family member removed."));
      clearFamilyRosterCache(); // CFTB-3347: keep the booking pickers' roster in sync.
      await loadMembers();
    } catch {
      setToastError(t("Could not remove the family member."));
    }
  };

  return (
    <AccountWrap>
      <div className={cls(styles.container, "panel")}>
        <div className={styles.header}>
          <h2 className={styles.panel_title}>{t("Family Management")}</h2>
          <Button
            buttonType="primary"
            title={t("Add Member")}
            icon={<i className="ico ico-add" />}
            onClickFunc={openCreate}
          />
        </div>

        <p className={styles.subtitle}>
          {t(
            "Manage family members linked to your account. Register them for programs, clinics, and events alongside yourself.",
          )}
        </p>

        <Loading isLoading={loading} loadType="spinner">
          {members.length === 0 ? (
            <div className={styles.empty_state}>
              <p>{t("You have not added any family members yet.")}</p>
            </div>
          ) : (
            <div className={styles.members}>
              {members.map((member) => (
                <FamilyMemberCard
                  key={member.id}
                  member={member}
                  onEdit={openEdit}
                  onDelete={requestDelete}
                />
              ))}
            </div>
          )}
        </Loading>
      </div>

      <FamilyMemberModal
        isOpen={modal.isOpen}
        mode={modal.mode}
        member={modal.member}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />

      <FamilyMemberAddedModal
        isOpen={Boolean(addedMemberName)}
        memberName={addedMemberName ?? ""}
        onClose={() => setAddedMemberName(null)}
      />

      <ModalLayout
        isOpen={Boolean(memberToDelete)}
        onClose={() => setMemberToDelete(null)}
        classNames={styles.confirm_popup}
        centerVertically
      >
        <div className={styles.confirm_container}>
          <h4 className={styles.confirm_title}>{t("Remove Family Member")}</h4>
          <p className={styles.confirm_text}>
            {t('Are you sure you want to remove "{{name}}"?', {
              name: memberToDelete
                ? `${memberToDelete.firstName} ${memberToDelete.lastName}`.trim()
                : "",
            })}
          </p>
          <div className={styles.confirm_footer}>
            <Button
              buttonType="secondary-outline"
              title={t("Cancel")}
              onClickFunc={() => setMemberToDelete(null)}
            />
            <Button
              buttonType="danger"
              title={t("Remove")}
              onClickFunc={confirmDelete}
            />
          </div>
        </div>
      </ModalLayout>

      <ModalLayout
        isOpen={Boolean(removalBlocked)}
        onClose={() => setRemovalBlocked(null)}
        classNames={styles.confirm_popup}
        centerVertically
      >
        <div className={styles.confirm_container}>
          <h4 className={styles.confirm_title}>
            {t("Can't remove family member")}
          </h4>
          <p className={styles.confirm_text}>
            {t(
              '"{{name}}" still has {{reasons}}. Cancel their upcoming sessions and registrations, and let any active packages lapse, before removing them.',
              {
                name: removalBlocked
                  ? `${removalBlocked.member.firstName} ${removalBlocked.member.lastName}`.trim()
                  : "",
                reasons: removalBlocked
                  ? blockedReasons(removalBlocked.blockers)
                  : "",
              },
            )}
          </p>
          <div className={styles.confirm_footer}>
            <Button
              buttonType="primary"
              title={t("OK")}
              onClickFunc={() => setRemovalBlocked(null)}
            />
          </div>
        </div>
      </ModalLayout>

      <Toast
        success={toastAlert}
        error={toastError}
        onDone={() => {
          setToastAlert(null);
          setToastError(null);
        }}
      />
    </AccountWrap>
  );
};

export default FamilyEdit;
